export const INSIGHTS_HUB_SCHEMA = {
  en: {
    hero: {
      heading: "Understand the bigger picture behind your money",
      description:
        "Financial Insights helps you see the habits, patterns, and decisions shaping your financial life — so you can move forward with more clarity, consistency, and confidence.",
      primary_cta: "Start Free",
      secondary_cta: "SEE PLANS",
      image_alt: "Financial Insight Hero",
    },
    subscriber_view: {
      money_guardian: {
        title: "Money Guardian",
        spend_safeguards: {
          title: "Spend Safeguards",
          desc_tooltip:
            "Keep an eye on unusual spending. Set system baselines and custom rules to catch high-amount or high-frequency transaction outflows.",
          threshold_tooltip:
            "Transactions exceeding this threshold amount will trigger an automated alert.",
          add_rule_cta: "Add Rule",
          unsubscribe_add_rule_msg: "Do you want personalized rules?",
          unsubscribe_add_rule_cta: "Upgrade Plan",
          unsubscribe_add_rule_marketing:
            "Unlock premium access to create up to 5 custom rules. Tailor your safeguards by Target Institution, Specific Accounts, Amount, and Spending Categories.",
        },
        credit_safeguards: {
          title: "Credit Safeguards",
          desc_tooltip:
            "Select one or more credit cards, set a utilization threshold, and get notified when your balance reaches it—helping you keep your credit utilization under control.",
          threshold_tooltip:
            "The balance amount at which an alert will be triggered for your selected credit cards.",
          add_rule_cta: "Add Rule",
          unsubscribe_add_rule_msg: "Do you want credit safeguards?",
          unsubscribe_add_rule_cta: "Upgrade Plan",
          unsubscribe_add_rule_marketing:
            "Unlock premium access to monitor your credit cards and get alerts when your balance reaches your chosen utilization threshold.",
        },
        labels: {
          system: "System",
          custom: "Custom",
          enabled: "Enabled",
          disabled: "Disabled",
          no_rules: "No transaction rules set up.",
        },
        actions: {
          update: "Update",
          delete: "Delete",
        },
        errors: {
          load_fail: "Failed to load guardian rules.",
          unexpected: "An unexpected error occurred. Please try again.",
          toggle_fail: "Failed to modify configuration switch setting.",
          delete_fail: "Failed to delete custom safeguard rule.",
        },
        modals: {
          cancel_cta: "Cancel",
          processing_cta: "Processing",
          deleting_cta: "Deleting",
          toggle_message: 'Are you sure you want to {action} "{name}"?',
          delete_message:
            'Are you sure you want to permanently delete the custom safeguard rule "{name}"?.',
          create_edit_modal: {
            title_create: "Create Spend Safeguard Rule",
            title_edit: "Update Spend Safeguard Rule",
            alert_fail_create: "Creation Failed",
            alert_fail_edit: "Update Failed",
            field_name: "Rule Name",
            field_name_placeholder: "e.g., High Outflow Alert",
            field_amount: "Trigger Amount ($)",
            field_amount_placeholder: "500.00",
            field_description: "Description",
            field_description_placeholder:
              "Describe the primary context notification parameters for this safeguard...",
            filters_heading: "Filters Override (Optional)",
            filter_institutions: "Target Financial Institutions",
            filter_institutions_placeholder: "All Connected Institutions",
            filter_accounts: "Specific Accounts",
            filter_accounts_placeholder: "All Available Accounts",
            filter_categories: "Tracked Spending Categories",
            filter_categories_placeholder: "All Spending Categories",
            account_fallback_label: "Unnamed Account",
            institution_fallback_label: "Unknown Institution",
            cta_save: "Save Rule",
            cta_update: "Update Rule",
            cta_submitting: "Saving...",
          },
        },
      },
      reports: {
        title: "Reports",
        monthly_reports: {
          title: "Monthly Reports",
          card_title_prefix: "Summary",
          card_period: "Period",
          summary_link: "View Summary",
          empty_report_msg: "No financial report summaries generated yet",
          monthly_desc_tooltip:
            "Monthly execution summaries and transactional insights.",
          snapshot_detail_modal: {
            income: "Total Income",
            spending: "Total Spending",
            cashflow: "Net Cashflow",
            categories: "Top Categories",
            non_category: "No category breakdown available",
            merchants: "Top Merchants",
            non_merchants: "No merchant breakdown available",
            pdf_card: {
              title: "Download Full PDF Report",
              pro_desc:
                "Includes full transactional appendices and category trends.",
              free_desc:
                "Upgrade to Pro to download formal PDF financial statements.",
              upgrade_btn: "Upgrade Plan",
            },
          },
        },
      },
    },
    value_cards: [
      {
        icon: "/images/icons/patterns_blue.svg",
        icon_alt: "Insights Hub",
        title: "Understand Patterns",
        description:
          "See how your financial habits evolve over time across spending, subscriptions, budgets, and everyday decisions.",
      },
      {
        icon: "/images/icons/tracking_progress.svg",
        icon_alt: "Insights Hub",
        title: "Track Progress",
        description:
          "Measure consistency, spot trends early, and understand how your behavior changes over time.",
      },
      {
        icon: "/images/icons/security_desicion.svg",
        icon_alt: "Insights Hub",
        title: "Make Smarter Decisions",
        description:
          "Get the context behind your finances so your decisions feel informed — not reactive.",
      },
    ],
    tools_intro: {
      heading: "Everything you need to better understand your money.",
      description:
        "Each tool gives you a different perspective on your financial life — so you can connect the dots, spot patterns, and make more informed decisions over time.",
      connect_bank_cta: "Connect a Bank to Get Started",
    },
    tools: [
      {
        key: "BUDGETING",
        label: "Budgeting",
        heading_color: "#83B50D",
        bg_image: "budget_hero.png",
        heading: "Plan your spending before it controls you.",
        description:
          "Create budgets that fit your lifestyle, track your progress in real time, and understand where your money is actually going.",
        features: [
          "Create budgets around your spending habits",
          "Track progress across categories",
          "See remaining balances in real time",
        ],
        cta: "Learn About Budgeting",
      },
      {
        key: "TRANSACTIONS",
        label: "Transactions",
        heading_color: "#0587FF",
        bg_image: "transaction_hero.png",
        heading: "Every dollar in. Every dollar out.",
        description:
          "See your transactions clearly across accounts, merchants, and categories — without guessing where your money went.",
        features: [
          "Smart transaction categorization",
          "Search and filter instantly",
          "Full transaction history",
        ],
        cta: "Learn About Transactions",
      },
      {
        key: "SPENDINGS",
        label: "Spending",
        heading_color: "#FF380A",
        bg_image: "spending_hero.png",
        heading: "See the patterns behind your spending habits.",
        description:
          "Understand how your spending evolves across categories, accounts, and time periods so you can identify habits early.",
        features: [
          "Category and account breakdowns",
          "Monthly spending comparisons",
          "Spending trend visibility",
        ],
        cta: "Learn About Spending Patterns",
      },
      {
        key: "RECURRINGS",
        label: "Recurring",
        heading_color: "#DBB000",
        bg_image: "recurring_hero.png",
        heading: "Know what keeps charging you.",
        description:
          "Track recurring subscriptions and payments before they quietly pile up in the background.",
        features: [
          "Subscription detection",
          "Upcoming recurring payments",
          "Annual recurring cost visibility",
        ],
        cta: "Learn About Recurring Payments",
      },
    ],
    tool_card_check_icon_alt: "Card Icon",
    pricing: {
      title: "Financial Insights",
      description:
        "Help users better understand and manage their money flow by tracking spending, income, budgets, savings goals, and upcoming financial activity in one place.",
      billing_intervals: {
        monthly: { label: "MONTHLY" },
        quarterly: { label: "QUARTERLY", savings: "Save 5%" },
        "6_months": { label: "6 MONTHS", savings: "Save 10%" },
        "12_months": { label: "12 MONTHS", savings: "Save 15%" },
        semi_annual: { label: "6 MONTHS", savings: "Save 10%" },
        annual: { label: "12 MONTHS", savings: "Save 15%" },
      },
      most_popular_badge: "MOST POPULAR",
    },
    plans: {
      free: {
        name: "Free",
        description: "Explore your financial habits with essential tools.",
        cta: "START FREE",
        features: [
          {
            text: "Connected Institutions ",
            strongText: "(Only 1)",
            isAvailable: true,
          },
          {
            text: "Account balances, credit usage & limits",
            isAvailable: true,
          },
          {
            text: "Financial Overview",
            isAvailable: true,
          },
          {
            text: "Transactions & History",
            isAvailable: true,
          },
          {
            text: "Recurring Transactions",
            isAvailable: true,
          },
          {
            text: "Budgets",
            strongText: " (Only 1)",
            isAvailable: true,
          },
          {
            text: "Budget History & Progress",
            isAvailable: true,
          },
          {
            text: "Monthly Reports",
            isAvailable: true,
          },
          {
            text: "Money Guardian",
            strongText: " (Only Default Safeguard Rule)",
            isAvailable: true,
          },
        ],
      },
      paid: {
        name: "Plus",
        description: "Unlock deeper insights and advanced financial tools.",
        cta: "Start Membership",
        features: [
          {
            text: "Connected Institutions ",
            strongText: " (Unlimited)",
            isAvailable: true,
          },
          {
            text: "Account balances, credit usage & limits",
            isAvailable: true,
          },
          {
            text: "Financial Overview",
            isAvailable: true,
          },
          {
            text: "Transactions & History",
            isAvailable: true,
          },
          {
            text: "Recurring Transactions",
            isAvailable: true,
          },
          {
            text: "Budgets",
            strongText: " (Unlimited)",
            isAvailable: true,
          },
          {
            text: "Budget History & Progress",
            isAvailable: true,
          },
          {
            text: "Monthly Reports",
            isAvailable: true,
          },
          {
            text: "Money Guardian",
            strongText: " (Custom Safeguards Rules)",
            isAvailable: true,
          },
        ],
      },
    },
    faq: {
      title: "Your Questions Answered",
      items: [
        {
          title: "How does Financial Insights work?",
          description:
            "Connect your financial accounts through Plaid to automatically organize your transactions, budgets, recurring payments, and spending activity in one place.",
        },
        {
          title: "Is my financial data secure?",
          description:
            "Your accounts are connected through Plaid, one of the most widely used financial connectivity platforms. Plaintr does not store your bank credentials.",
        },
        {
          title: "Can I disconnect my bank account later?",
          description:
            "Yes. Disconnecting an institution stops new data syncing, but your previously imported financial history remains available unless you permanently delete the institution.",
        },
        {
          title: "What happens if I reconnect an institution?",
          description:
            "Plaintr automatically resynchronizes your account data while handling duplicates and transaction replacements in the background.",
        },
        {
          title: "Can I create multiple budgets?",
          description:
            "Yes. Budgets can be created around categories, institutions, or broader spending activity depending on how you prefer to manage your finances.",
        },
        {
          title: "Do pending transactions count in Spendings?",
          description:
            "No. Spending totals only include posted transactions that meet the platform's spending qualification rules.",
        },
        {
          title: "Will my subscriptions be detected automatically?",
          description:
            "Recurring payments and subscriptions are automatically identified based on transaction behavior patterns across your connected accounts.",
        },
      ],
    },
  },
};
